import { ImagePlus, Trash2, RefreshCw } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { FormLabel } from "@/components/ui/form";

const MAX_PHOTO_SIZE_BYTES = 2 * 1024 * 1024; // 2MB

interface ProductPhotoUploadProps {
  photoUrl: string | null;
  isUploading: boolean;
  onSelectPhoto: (file: File) => void;
  onRemovePhoto: () => void;
}

export function ProductPhotoUpload({
  photoUrl,
  isUploading,
  onSelectPhoto,
  onRemovePhoto,
}: ProductPhotoUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  function validateFile(file: File): boolean {
    if (!file.type.startsWith("image/")) {
      toast.error("Hanya file gambar (JPG, PNG, WEBP) yang didukung.");
      return false;
    }
    if (file.size > MAX_PHOTO_SIZE_BYTES) {
      toast.error("Ukuran file maksimal 2MB.");
      return false;
    }
    return true;
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (validateFile(file)) onSelectPhoto(file);
    // Reset file input so re-selecting the same file triggers change
    e.target.value = "";
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (validateFile(file)) onSelectPhoto(file);
  }

  return (
    <div className="space-y-2">
      <FormLabel>Foto Produk</FormLabel>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/jpg"
        className="hidden"
        onChange={handleFileChange}
        disabled={isUploading}
      />

      {photoUrl ? (
        <div className="flex items-center gap-4 rounded-lg border bg-muted/20 p-3">
          <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-md border bg-background">
            <img
              src={photoUrl}
              alt="Foto produk"
              className="h-full w-full object-cover"
            />
          </div>
          <div className="flex flex-col gap-1.5 min-w-0 flex-1">
            <span className="text-xs text-muted-foreground font-medium">
              Foto produk dipilih
            </span>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                Ganti Foto
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={onRemovePhoto}
                disabled={isUploading}
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                Hapus
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setIsDragging(false);
          }}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-4 text-center cursor-pointer transition-colors ${
            isDragging
              ? "border-primary bg-primary/10"
              : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/30"
          }`}
        >
          <div className="rounded-full bg-primary/10 p-2.5 text-primary">
            <ImagePlus className="h-5 w-5" />
          </div>
          <div className="space-y-0.5">
            <p className="text-xs font-medium">Klik atau seret foto ke sini</p>
            <p className="text-[11px] text-muted-foreground">
              Format PNG, JPG, WEBP (maks. 2MB)
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
